import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  BrandGrant,
  Capability,
  CurrentUser,
  OrganizationGrant,
  SessionResponse,
} from "@velivo/shared";
import { api, ApiRequestError, tokenStore } from "../api/client";

const LAST_BRAND_KEY = "echo.lastBrandId";

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  /** The Brand currently in context — drives every Brand-scoped screen. */
  activeBrand: BrandGrant | null;
  setActiveBrand: (brandId: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  applySession: (session: SessionResponse) => void;
  refreshUser: () => Promise<void>;
  /** Capability check for rendering. NEVER the enforcement point — the server
   *  re-checks every request (FR-ADMIN-02). Hiding a control the API would
   *  reject is a courtesy, not a security boundary. */
  can: (capability: Capability) => boolean;
  /**
   * The Organization-scope counterpart to `can`, resolved against the active
   * Brand's Organization.
   *
   * Kept separate rather than folded into `can` because conflating the two is
   * the exact confusion this screen exists to remove: `brand:manage_members`
   * and `org:manage_members` grant different things at different levels, and a
   * Brand Admin holds the first and not the second.
   */
  canOrg: (capability: Capability) => boolean;
  /** The user's Organization grant covering the active Brand, if any. */
  activeOrganization: OrganizationGrant | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeBrandId, setActiveBrandId] = useState<string | null>(() =>
    localStorage.getItem(LAST_BRAND_KEY),
  );

  const loadUser = useCallback(async () => {
    if (!tokenStore.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api<CurrentUser>("/auth/me"));
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) {
        tokenStore.clear();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const applySession = useCallback((session: SessionResponse) => {
    tokenStore.set(session.accessToken, session.refreshToken);
    setUser(session.user);
    setLoading(false);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await api<SessionResponse>("/auth/login", {
        method: "POST",
        body: { email, password },
        auth: false,
      });
      applySession(session);
    },
    [applySession],
  );

  const signOut = useCallback(async () => {
    try {
      await api("/auth/logout", {
        method: "POST",
        body: { refreshToken: tokenStore.refresh },
      });
    } catch {
      // A failed logout call must still clear local state — otherwise a network
      // blip leaves the user apparently signed in.
    }
    tokenStore.clear();
    localStorage.removeItem(LAST_BRAND_KEY);
    setActiveBrandId(null);
    setUser(null);
  }, []);

  /**
   * FR-AUTH-05: land on the single Brand when there's exactly one, otherwise
   * the last-used Brand, otherwise let the picker decide. Never show a
   * selection screen to someone with only one option.
   */
  const activeBrand = useMemo<BrandGrant | null>(() => {
    if (!user || user.brandGrants.length === 0) return null;
    if (user.brandGrants.length === 1) return user.brandGrants[0]!;
    const remembered = user.brandGrants.find(
      (g) => g.brandId === activeBrandId,
    );
    return remembered ?? null;
  }, [user, activeBrandId]);

  const setActiveBrand = useCallback((brandId: string) => {
    localStorage.setItem(LAST_BRAND_KEY, brandId);
    setActiveBrandId(brandId);
  }, []);

  const can = useCallback(
    (capability: Capability) =>
      activeBrand?.capabilities.includes(capability) ?? false,
    [activeBrand],
  );

  // Scoped to the active Brand's Organization, not "any Organization the user
  // admins" — an agency user who is OrgAdmin of one Organization must not see
  // Organization controls while working inside another Organization's Brand.
  const activeOrganization = useMemo(
    () =>
      user?.organizationGrants.find(
        (grant) => grant.organizationId === activeBrand?.organizationId,
      ) ?? null,
    [user, activeBrand],
  );

  const canOrg = useCallback(
    (capability: Capability) =>
      activeOrganization?.capabilities.includes(capability) ?? false,
    [activeOrganization],
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      activeBrand,
      setActiveBrand,
      signIn,
      signOut,
      applySession,
      refreshUser: loadUser,
      can,
      canOrg,
      activeOrganization,
    }),
    [
      user,
      loading,
      activeBrand,
      setActiveBrand,
      signIn,
      signOut,
      applySession,
      loadUser,
      can,
      canOrg,
      activeOrganization,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
